import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import * as api from "../../../../api/generated-api";
import { DealDocument, DealDocumentType } from "../../../../api/generated-api";
import {
  asyncCreateDealDocument,
  asyncCreateDealDocumentVersion,
  deleteDealDocument,
  downloadExecutedFile,
  getDealDocuments,
} from "../../../../api/deals/documents";
import {
  configureAuthHeader,
  configureBaseUrl,
} from "../../../../api/axiosConfig";
import { Box, Typography } from "@mui/material";
import {
  isCompanyMemberOrCounselForDeal,
  toValidIdentifier,
} from "../../../../helpers/utilsTS";
import { getDealDocumentSignatures } from "../../../../api/deals/signatures";
import {
  getAllDealLegalEntityInvestmentsForDeal,
  getAllDealLegalEntityInvestmentsForDealAsync,
} from "../../../../api/deals/investors";
import { DealMemberSigner } from "../../closing/signers/signersPanel/DocumentsSignersGroup";
import { processUnexpectedError } from "../../../../api/errors";
import SignatureAssignmentRightSidebar from "../../closing/signers/signersPanel/SignatureAssignmentRightSidebar";
import { DealsPageLayout } from "../../dealsPageLayout/DealsPageLayout";
import {
  getDealLegalEntitiesForDeal,
  getDealLegalEntitiesForUser,
} from "../../../../api/deals/deallegalentities";
import { UserWithPersonWithOrganizationPersons } from "../../../../api/types";
import { getDealLawFirms } from "../../../../api/deals/lawfirms";
import { UploadDocumentModal } from "./UploadDocumentModal";
import { ProgressIndicator } from "../../../common/progress/ProgressIndicator";
import {
  DealDocumentRowContainer,
  DealDocumentRowItem,
  DealDocumentRowItemProps,
} from "./DealDocumentRowItem";
import { COIDealDocumentRowItem } from "./COIDealDocumentRowItem";
import { ScheduleOfPurchasersRowItem } from "./ScheduleOfPurchasersRowItem";
import { DealDocumentTypeDisplay } from "./DealDocumentTypeDisplay";
import { DealsPageEmptyState } from "../../dealsPageLayout/DealsPageEmptyState/DealsPageEmptyState";

type DealDocumentsProps = {
  token: string;
  user: UserWithPersonWithOrganizationPersons;
  orgId: number;
  dealId: number;
};

type DealDocumentTypeSection = {
  title: string;
  types: api.DealDocumentType[];
};

export type DealLegalEntityWithInvestmentTypes = api.DealLegalEntity & {
  hasNewCashInvestment: boolean;
  hasSafeInvestment: boolean;
};

export const mainFinancingDocumentTypes = [
  api.DealDocumentType.Certificate_of_Incorporation,
  api.DealDocumentType.Stock_Purchase_Agreement,
  api.DealDocumentType["Investors'_Rights_Agreement"],
  api.DealDocumentType.Voting_Agreement,
  api.DealDocumentType["Right_of_First_Refusal_and_Co-Sale_Agreement"],
];

export const consentsDocumentTypes = [
  api.DealDocumentType.Board_Consent,
  api.DealDocumentType.Stockholder_Consent,
];

export const closingDocumentTypes = [
  api.DealDocumentType.Compliance_Certificate,
  api.DealDocumentType["Secretary's_Certificate"],
  api.DealDocumentType.Indemnification_Agreement,
  api.DealDocumentType.Management_Rights_Letter,
  api.DealDocumentType.Disclosure_Schedule,
  api.DealDocumentType.Legal_Opinion,
  api.DealDocumentType.Opinion_Certificate,
];

const allDocumentTypes: DealDocumentTypeSection[] = [
  { title: "Main Financing Documents", types: mainFinancingDocumentTypes },
  { title: "Consents And Approvals", types: consentsDocumentTypes },
  { title: "Closing Deliverables", types: closingDocumentTypes },
  { title: "Other", types: [api.DealDocumentType.Other] },
];

const DealDocuments = ({ orgId, dealId, token, user }: DealDocumentsProps) => {
  configureBaseUrl();
  configureAuthHeader(token);

  const documentFileRefs = new Map<
    DealDocumentType,
    MutableRefObject<HTMLInputElement>
  >();

  [
    ...mainFinancingDocumentTypes,
    ...consentsDocumentTypes,
    ...closingDocumentTypes,
  ].forEach((docType: api.DealDocumentType) => {
    documentFileRefs.set(docType, useRef<HTMLInputElement | null>(null));
  });

  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const [showProgressIndicator, setShowProgressIndicator] =
    React.useState<boolean>(false);
  const [dealDocumentsByType, setDealDocumentsByType] = useState<
    Map<api.DealDocumentType, api.DealDocument[]>
  >(new Map());
  const [unsafeToDeleteDocumentIds, setUnsafeToDeleteDocumentIds] =
    useState<number[]>();
  const [uploadingDocumentType, setUploadingDocumentType] =
    useState<api.DealDocumentType>();
  const [
    scheduleOfPurchasersDownloadable,
    setScheduleOfPurchasersDownloadable,
  ] = useState<boolean>(false);
  const [signerPanelDocument, setSignerPanelDocument] =
    useState<api.DealDocument>(null);
  const [dealDocumentsSignatures, setDealDocumentsSignatures] =
    useState<api.DealDocumentSignature[]>(undefined);
  const [
    dealLegalEntitiesWithInvestmentTypes,
    setDealLegalEntitiesWithInvestmentTypes,
  ] = useState<DealLegalEntityWithInvestmentTypes[]>([]);
  const [dealLegalEntitiesForUser, setDealLegalEntitiesForUser] = useState<
    api.DealLegalEntity[]
  >([]);
  const [dealLawFirms, setDealLawFirms] = useState<api.DealLawFirm[]>([]);

  useEffect(() => {
    getDealLegalEntitiesForUser(orgId, dealId, (fetchedDealLegalEntities) => {
      setDealLegalEntitiesForUser(fetchedDealLegalEntities);
    });
  }, [orgId, dealId]);

  const fetchAndSetDealDocuments = () => {
    getDealDocuments(orgId, dealId, (fetchedDealDocs) => {
      setUploadingDocumentType(undefined);

      const fromFetchDealDocsByType = new Map<
        api.DealDocumentType,
        api.DealDocument[]
      >();

      fetchedDealDocs.forEach((dealDocument) => {
        if (!fromFetchDealDocsByType.has(dealDocument.documentType)) {
          fromFetchDealDocsByType.set(dealDocument.documentType, []);
        }

        const docsByType = fromFetchDealDocsByType.get(
          dealDocument.documentType
        );
        docsByType.push(dealDocument);

        fromFetchDealDocsByType.set(dealDocument.documentType, docsByType);
      });
      setDealDocumentsByType(fromFetchDealDocsByType);
    });

    getDealDocumentSignatures(orgId, dealId, (fetchedDealSignatures) => {
      const fetchedUnsafeToDeleteDocumentIds = fetchedDealSignatures.map(
        (fetchedDealSigner) => {
          return fetchedDealSigner.dealDocumentId;
        }
      );

      setUnsafeToDeleteDocumentIds(fetchedUnsafeToDeleteDocumentIds);
    });

    getAllDealLegalEntityInvestmentsForDeal(
      orgId,
      dealId,
      (dealLegalEntityInvestments) => {
        const filtered = dealLegalEntityInvestments.filter((legalEntity) => {
          if (legalEntity.wireDateReceived || legalEntity.purchaseAmount) {
            return legalEntity;
          }
        });
        setScheduleOfPurchasersDownloadable(filtered && filtered.length > 0);
      }
    );

    getDealDocumentSignatures(orgId, dealId, (dealDocumentsSignatures) => {
      setDealDocumentsSignatures(dealDocumentsSignatures);
    });

    getDealLegalEntitiesForDeal(orgId, dealId, (fetchedDealLegalEntities) => {
      const dealLegalEntitiesForCompanyAndLawFirmMembers =
        fetchedDealLegalEntities.filter((dealLegalEntity) => {
          return dealLegalEntity.isCompany || dealLegalEntity.isLawFirm;
        });

      getDealLegalEntityInvestmentsForDeal(
        dealLegalEntitiesForCompanyAndLawFirmMembers
      );
    });
  };

  const getDealLegalEntityInvestmentsForDeal = async (
    dealLegalEntities: api.DealLegalEntity[]
  ) => {
    const dealLegalEntityInvestmentsForDeal =
      await getAllDealLegalEntityInvestmentsForDealAsync(orgId, dealId);

    const dealLegalEntitiesForInvestments: DealLegalEntityWithInvestmentTypes[] =
      dealLegalEntityInvestmentsForDeal.map((dealLegalEntityInvestment) => {
        return {
          ...dealLegalEntityInvestment.signer,
          hasNewCashInvestment:
            !!dealLegalEntityInvestment.targetCashInvestment,
          hasSafeInvestment: !!dealLegalEntityInvestment.purchaseAmount,
        };
      });

    const dealLegalEntitiesForCompanyMembers: DealLegalEntityWithInvestmentTypes[] =
      dealLegalEntities.map((dealLegalEntity) => {
        return {
          ...dealLegalEntity,
          hasNewCashInvestment: false,
          hasSafeInvestment: false,
        };
      });

    setDealLegalEntitiesWithInvestmentTypes([
      ...dealLegalEntitiesForCompanyMembers,
      ...dealLegalEntitiesForInvestments,
    ]);
  };

  useEffect(() => {
    fetchAndSetDealDocuments();

    getDealLegalEntitiesForUser(orgId, dealId, (fetchedDealLegalEntities) => {
      setDealLegalEntitiesForUser(fetchedDealLegalEntities);
    });

    getDealLawFirms(orgId, dealId, (fetchedDealLawFirms) => {
      setDealLawFirms(fetchedDealLawFirms);
    });
  }, [orgId, dealId]);

  const saveChosenFileToState = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: api.DealDocumentType,
    name?: string
  ) => {
    const target = event.target;
    if (!target.value?.length) {
      setUploadingDocumentType(undefined);
    } else {
      setUploadingDocumentType(uploadingDocumentType);
    }

    event.stopPropagation();
    event.preventDefault();
    const selectedFile = event.target.files[0];

    const response = await asyncCreateDealDocument(
      orgId,
      dealId,
      selectedFile,
      documentType,
      name
    );

    fetchAndSetDealDocuments();
    return response.data as DealDocument;
  };

  const saveChosenFileVersionToState = async (event, documentId: number) => {
    const target = event.target;
    if (!target.value?.length) {
      setUploadingDocumentType(undefined);
    } else {
      setUploadingDocumentType(uploadingDocumentType);
    }
    event.stopPropagation();
    event.preventDefault();
    const selectedFile = event.target.files[0];
    const response = await asyncCreateDealDocumentVersion(
      orgId,
      dealId,
      documentId,
      selectedFile
    );
    fetchAndSetDealDocuments();
    return response.data as DealDocument;
  };

  const handleDeleteDealDocument = (dealDocumentId: number) => {
    deleteDealDocument(orgId, dealId, dealDocumentId, () => {
      fetchAndSetDealDocuments();
    });
  };

  const handleOpenSignersPanelForDocument = (
    dealDocument: api.DealDocument
  ) => {
    setSignerPanelDocument(dealDocument);
  };

  const handleAddSigners = async (
    addedDealMemberSigners: DealMemberSigner[],
    dealDocumentId: number
  ) => {
    const newDealDocumentSignaturesBody: api.DealDocumentSignatureCreate[] =
      addedDealMemberSigners.map((addedDealMemberSignerData) => {
        return {
          dealLegalEntityId: addedDealMemberSignerData.dealLegalEntityId,
          status: "notRequested",
        };
      });

    try {
      const { data: newDealDocumentSignatures } =
        await api.createDealDocumentSignature(
          orgId,
          dealId,
          dealDocumentId,
          newDealDocumentSignaturesBody
        );

      setDealDocumentsSignatures([
        ...dealDocumentsSignatures,
        ...newDealDocumentSignatures,
      ]);
    } catch (error) {
      processUnexpectedError(error);
    }
  };

  const handleRemoveSigners = async (
    removedDealMemberSigners: DealMemberSigner[],
    dealDocumentId: number
  ) => {
    const dealDocumentSignaturesIdsToRemove: number[] =
      removedDealMemberSigners.map(
        (removedDealMemberSignerData) => removedDealMemberSignerData.signerId
      );

    try {
      const { status } = await api.removeDealDocumentSignature(
        orgId,
        dealId,
        dealDocumentId,
        dealDocumentSignaturesIdsToRemove
      );
      if (status === 204) {
        setDealDocumentsSignatures(
          [...dealDocumentsSignatures].filter(
            (dealDocumentSigner) =>
              !dealDocumentSignaturesIdsToRemove.includes(dealDocumentSigner.id)
          )
        );
      }
    } catch (error) {
      processUnexpectedError(error);
    }
  };

  if (!unsafeToDeleteDocumentIds) {
    return null;
  }

  const SignersPanelSidebar = () => {
    return (
      <SignatureAssignmentRightSidebar
        orgId={orgId}
        user={user}
        dealId={dealId}
        dealDocumentId={signerPanelDocument.id}
        dealDocumentSignatures={dealDocumentsSignatures.filter(
          (dealDocumentSigner) =>
            dealDocumentSigner.dealDocumentId === signerPanelDocument.id
        )}
        dealLegalEntitiesWithInvestmentTypes={
          dealLegalEntitiesWithInvestmentTypes
        }
        onAddSigners={handleAddSigners}
        onRemoveSigners={handleRemoveSigners}
        dealLegalEntitiesForUser={dealLegalEntitiesForUser}
        dealLawFirms={dealLawFirms}
      />
    );
  };

  if (!dealLegalEntitiesForUser) {
    return null;
  }

  const isUserCompanyMember = isCompanyMemberOrCounselForDeal(
    user.person.organizations_persons,
    orgId,
    dealId,
    dealLegalEntitiesForUser
  );

  const showEmptyState = () => {
    return !Array.from(dealDocumentsByType.values()).flat().length;
  };

  return (
    <DealsPageLayout
      title="Documents"
      rightHeaderAction={
        showProgressIndicator && (
          <ProgressIndicator size={20} dataTestId={"uploading-status-icon"} />
        )
      }
      sidebar={signerPanelDocument ? <SignersPanelSidebar /> : null}
      sidebarTitle={`Signers for ${signerPanelDocument?.name}`}
      sidebarOnClose={() => {
        setSignerPanelDocument(null);
        fetchAndSetDealDocuments();
      }}
      actionButtons={
        !showProgressIndicator && !signerPanelDocument && isUserCompanyMember
          ? [
              {
                label: "Add Document",
                primary: true,
                dataTestId: "add-document-button",
                onClick: () => {
                  setShowUploadDialog(true);
                },
              },
            ]
          : []
      }
    >
      {showEmptyState() && (
        <DealsPageEmptyState
          title={"Add the draft financing documents"}
          content={
            "They will be visible to everyone who has been invited to participate in the deal"
          }
        />
      )}
      {!showEmptyState() && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          {allDocumentTypes.map((section, index) => {
            return (
              <Box
                marginTop={index > 0 ? "30px" : undefined}
                key={toValidIdentifier(section.title)}
                data-testid={`deal-document-group-${toValidIdentifier(
                  section.title
                )}`}
              >
                <Typography variant="h2">{section.title}</Typography>
                {section.types.map((documentType) => {
                  const dealDocuments = dealDocumentsByType.has(documentType)
                    ? dealDocumentsByType.get(documentType)
                    : [];

                  if (!dealDocuments.length) {
                    return null;
                  }

                  const certificateOfIncorporation = dealDocumentsByType.get(
                    DealDocumentType.Certificate_of_Incorporation
                  );
                  const filedCertificateOfIncorporation =
                    dealDocumentsByType.get(
                      DealDocumentType.Filed_Certificate_of_Incorporation
                    );
                  return (
                    <>
                      <DealDocumentRowContainer>
                        <DealDocumentTypeDisplay
                          documentTypeName={documentType}
                        />
                      </DealDocumentRowContainer>
                      {dealDocuments.map((dealDocument) => {
                        const deletable =
                          dealDocument &&
                          !unsafeToDeleteDocumentIds.includes(
                            dealDocument.id
                          ) &&
                          isUserCompanyMember;
                        const uploadable = isUserCompanyMember;
                        const highlighted =
                          dealDocument &&
                          dealDocument?.id === signerPanelDocument?.id;

                        const documentRowItemProps: DealDocumentRowItemProps = {
                          documentType,
                          dealDocument,
                          dealId,
                          orgId,
                          uploadable,
                          deletable,
                          highlighted,
                          dealDocumentSignatures:
                            dealDocumentsSignatures?.filter(
                              (dealDocumentSigner) =>
                                dealDocumentSigner.dealDocumentId ===
                                dealDocument?.id
                            ),
                          onDocumentVersionUploadFileSelected:
                            saveChosenFileVersionToState,
                          onDocumentUploadFileCancel: () => {
                            setUploadingDocumentType(undefined);
                          },
                          onDocumentSignersClick:
                            handleOpenSignersPanelForDocument,
                          onDocumentDeleteClick: (documentIdToDelete) => {
                            handleDeleteDealDocument(documentIdToDelete);
                          },
                          onDocumentDownloadClick: (documentId) => {
                            downloadExecutedFile(orgId, dealId, documentId);
                          },
                        };

                        return (
                          <Box key={toValidIdentifier(documentType)}>
                            {documentType ===
                              DealDocumentType.Certificate_of_Incorporation && (
                              <COIDealDocumentRowItem
                                {...documentRowItemProps}
                                onDocumentUploadFileSelected={async (
                                  event,
                                  docType,
                                  name
                                ) => {
                                  return await saveChosenFileToState(
                                    event,
                                    docType,
                                    name
                                  );
                                }}
                                certificateOfIncorporation={
                                  certificateOfIncorporation &&
                                  certificateOfIncorporation.length
                                    ? certificateOfIncorporation[0]
                                    : undefined
                                }
                                filedCertificateOfIncorporation={
                                  filedCertificateOfIncorporation &&
                                  filedCertificateOfIncorporation.length
                                    ? filedCertificateOfIncorporation[0]
                                    : undefined
                                }
                              />
                            )}
                            {documentType !==
                              DealDocumentType.Certificate_of_Incorporation && (
                              <DealDocumentRowItem {...documentRowItemProps} />
                            )}
                          </Box>
                        );
                      })}
                    </>
                  );
                })}
                {section.title === "Closing Deliverables" &&
                  scheduleOfPurchasersDownloadable && (
                    <>
                      <DealDocumentRowContainer>
                        <DealDocumentTypeDisplay
                          documentTypeName={"Schedule of Purchasers"}
                        />
                      </DealDocumentRowContainer>
                      <Box key={toValidIdentifier("Schedule of Purchasers")}>
                        <ScheduleOfPurchasersRowItem
                          orgId={orgId}
                          dealId={dealId}
                          showActions={!signerPanelDocument}
                        />
                      </Box>
                    </>
                  )}
              </Box>
            );
          })}
        </Box>
      )}
      <UploadDocumentModal
        dataTestId={"deal-document-picker"}
        open={showUploadDialog}
        documentTypes={
          dealDocumentsByType.has(DealDocumentType.Certificate_of_Incorporation)
            ? [
                ...mainFinancingDocumentTypes.filter(
                  (docType) =>
                    docType != DealDocumentType.Certificate_of_Incorporation
                ),
                ...consentsDocumentTypes,
                ...closingDocumentTypes,
                DealDocumentType.Other,
              ]
            : [
                ...mainFinancingDocumentTypes,
                ...consentsDocumentTypes,
                ...closingDocumentTypes,
                DealDocumentType.Other,
              ]
        }
        onCancel={() => {
          setShowUploadDialog(false);
        }}
        onDocumentSave={async (event, docType, fileName) => {
          setShowUploadDialog(false);
          setShowProgressIndicator(true);
          const uploadedDocument = await saveChosenFileToState(
            event,
            docType,
            fileName
          );
          setShowProgressIndicator(false);
          return uploadedDocument;
        }}
      />
    </DealsPageLayout>
  );
};

export default DealDocuments;
